/**
 * Baseline snapshots + field diffs for order-form customer create/update UX.
 */

export function t(v) {
    return String(v ?? "").trim()
}

/** Contact fields compared for "edited vs loaded customer". */
export const CONTACT_DIFF_FIELDS = [
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "companyName", label: "Company Name" },
    { key: "contact_method", label: "Preferred Contact Method" },
    { key: "is_staff", label: "Is Staff?", kind: "bool" },
    { key: "tripCode", label: "Trip Code" },
    { key: "fishTag", label: "Fish Tag" },
    { key: "licenseNumber", label: "Fishing License Number" },
]

export const ADDRESS_DIFF_FIELDS = [
    { key: "firstName", label: "First Name" },
    { key: "lastName", label: "Last Name" },
    { key: "companyName", label: "Company Name" },
    { key: "email", label: "Email" },
    { key: "country", label: "Country" },
    { key: "province", label: "Province / State" },
    { key: "city", label: "City" },
    { key: "streetAddress", label: "Street Address" },
    { key: "postalCode", label: "Postal / Zip" },
    { key: "phone", label: "Phone" },
]

export function snapshotContactBaseline(v) {
    return {
        firstName: t(v.firstName),
        lastName: t(v.lastName),
        email: t(v.email),
        phone: t(v.phone),
        companyName: t(v.companyName),
        contact_method: t(v.contact_method) || "email",
        is_staff: Boolean(v.is_staff),
        tripCode: t(v.tripCode),
        fishTag: t(v.fishTag),
        licenseNumber: t(v.licenseNumber),
        licenseFrontImg: v.licenseFrontImg ?? null,
        licenseBackImg: v.licenseBackImg ?? null,
        hadLicenseFront: Boolean(v.licenseFrontImg) || v.licenseFileFront instanceof File,
        hadLicenseBack: Boolean(v.licenseBackImg) || v.licenseFileBack instanceof File,
    }
}

export function snapshotAddressBaseline(v) {
    const snap = {}
    for (const f of ADDRESS_DIFF_FIELDS) {
        snap[f.key] = t(v[f.key])
    }
    snap.defaultAddressId = v.defaultShipAddressId ?? v.defaultBillAddressId ?? null
    return snap
}

/** True when contact has any user-entered detail (for "customer will be created" banner). */
export function contactHasAnyDetail(v) {
    const keys = [
        "firstName",
        "lastName",
        "email",
        "phone",
        "companyName",
        "tripCode",
        "fishTag",
        "licenseNumber",
    ]
    if (keys.some((k) => t(v[k]) !== "")) {
        return true
    }
    if (Boolean(v.is_staff)) {
        return true
    }
    if (v.licenseFileFront instanceof File || v.licenseFileBack instanceof File) {
        return true
    }
    if (v.licenseFrontImg || v.licenseBackImg) {
        return true
    }
    return false
}

function formatBaselineValue(field, baseline) {
    if (field.kind === "bool") {
        return baseline[field.key] ? "Yes" : "No"
    }
    const s = t(baseline[field.key])
    return s === "" ? "(empty)" : s
}

/**
 * @returns {{ key: string, label: string, fromDisplay: string, kind?: string }[]}
 */
export function getContactFieldDiffs(value, baseline) {
    if (!baseline) {
        return []
    }
    /** @type {{ key: string, label: string, fromDisplay: string, kind?: string }[]} */
    const diffs = []
    for (const f of CONTACT_DIFF_FIELDS) {
        if (f.kind === "bool") {
            if (Boolean(value[f.key]) !== Boolean(baseline[f.key])) {
                diffs.push({
                    key: f.key,
                    label: f.label,
                    fromDisplay: formatBaselineValue(f, baseline),
                })
            }
            continue
        }
        if (t(value[f.key]) !== t(baseline[f.key])) {
            diffs.push({
                key: f.key,
                label: f.label,
                fromDisplay: formatBaselineValue(f, baseline),
            })
        }
    }

    const front = photoDiff("front", value, baseline)
    const back = photoDiff("back", value, baseline)
    if (front) {
        diffs.push(front)
    }
    if (back) {
        diffs.push(back)
    }

    return diffs
}

function photoDiff(side, value, baseline) {
    const had = side === "front" ? Boolean(baseline.hadLicenseFront) : Boolean(baseline.hadLicenseBack)
    const basePath = side === "front" ? baseline.licenseFrontImg : baseline.licenseBackImg
    const nowPath = side === "front" ? value.licenseFrontImg : value.licenseBackImg
    const nowFile = side === "front" ? value.licenseFileFront : value.licenseFileBack
    const nowHas = Boolean(nowPath) || nowFile instanceof File
    const key = side === "front" ? "licenseFront" : "licenseBack"
    const label = side === "front" ? "License Front Photo" : "License Back Photo"

    if (!had && nowHas) {
        return { key, label, fromDisplay: "", kind: "photo_added" }
    }
    if (had && !nowHas) {
        return { key, label, fromDisplay: "", kind: "photo_removed" }
    }
    if (had && nowHas && (nowFile instanceof File || t(nowPath) !== t(basePath))) {
        return { key, label, fromDisplay: "", kind: "photo_replaced" }
    }
    return null
}

export function contactHasEdits(value, baseline) {
    return getContactFieldDiffs(value, baseline).length > 0
}

export function getAddressFieldDiffs(value, baseline) {
    if (!baseline) {
        return []
    }
    return ADDRESS_DIFF_FIELDS.filter((f) => t(value[f.key]) !== t(baseline[f.key])).map((f) => ({
        key: f.key,
        label: f.label,
        fromDisplay: t(baseline[f.key]) === "" ? "(empty)" : t(baseline[f.key]),
    }))
}

/** Person fields soft-mirrored from contact → shipping while creating a new customer. */
export const ADDRESS_PERSON_MIRROR_KEYS = [
    "firstName",
    "lastName",
    "companyName",
    "phone",
    "email",
]

/**
 * Diffs for shipping person fields against contact (soft-mirror baseline).
 * @returns {{ key: string, label: string, fromDisplay: string }[]}
 */
export function getAddressPersonFieldDiffs(value, contactPersonBaseline) {
    if (!contactPersonBaseline) {
        return []
    }
    /** @type {{ key: string, label: string, fromDisplay: string }[]} */
    const diffs = []
    for (const key of ADDRESS_PERSON_MIRROR_KEYS) {
        if (t(value[key]) === t(contactPersonBaseline[key])) {
            continue
        }
        const field = ADDRESS_DIFF_FIELDS.find((f) => f.key === key)
        diffs.push({
            key,
            label: field?.label ?? key,
            fromDisplay:
                t(contactPersonBaseline[key]) === "" ?
                    "(empty)" :
                    t(contactPersonBaseline[key]),
        })
    }
    return diffs
}

export function addressHasEdits(value, baseline) {
    return getAddressFieldDiffs(value, baseline).length > 0
}

export function addressHasAnyDetail(v) {
    return ADDRESS_DIFF_FIELDS.some((f) => t(v[f.key]) !== "")
}

/** Undo one contact field (or photo) back to baseline. */
export function undoContactField(value, baseline, key) {
    if (!baseline) {
        return value
    }
    if (key === "licenseFront") {
        return {
            ...value,
            licenseFrontImg: baseline.licenseFrontImg,
            licenseFileFront: null,
            licenseFile: value.licenseFileBack ?? null,
        }
    }
    if (key === "licenseBack") {
        return {
            ...value,
            licenseBackImg: baseline.licenseBackImg,
            licenseFileBack: null,
            licenseFile: value.licenseFileFront ?? null,
        }
    }
    const field = CONTACT_DIFF_FIELDS.find((f) => f.key === key)
    if (!field) {
        return value
    }
    return { ...value, [key]: baseline[key] }
}

export function undoAddressField(value, baseline, key) {
    if (!baseline || !(key in baseline)) {
        return value
    }
    return { ...value, [key]: baseline[key], addressDirty: true }
}

export function photoChangeMessage(kind) {
    if (kind === "photo_added") {
        return "Photo added"
    }
    if (kind === "photo_removed") {
        return "Photo removed"
    }
    if (kind === "photo_replaced") {
        return "Photo replaced"
    }
    return "Updated"
}
